import React from 'react';
import { KycSubmission } from '../../services/adminApiService';
import { API_BASE } from '../../constants/app.config';
import './AdminComponents.css';
import { FilePreviewModal } from '../common/FilePreviewModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kyc-tabpanel-${index}`}
      aria-labelledby={`kyc-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="kyc-tab-content">
          {children}
        </div>
      )}
    </div>
  );
}

interface KycDetailsModalProps {
  open: boolean;
  onClose: () => void;
  submission: any | null;
}

const KycDetailsModal: React.FC<KycDetailsModalProps> = ({ open, onClose, submission }) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [previewFile, setPreviewFile] = React.useState<{
    isOpen: boolean;
    url: string;
    fileName: string;
    contentType?: string;
  }>({
    isOpen: false,
    url: '',
    fileName: '',
    contentType: undefined
  });

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return 'status-badge approved';
      case 'REJECTED': return 'status-badge rejected';
      case 'PENDING': return 'status-badge pending';
      case 'IN_PROGRESS': return 'status-badge in-progress';
      default: return 'status-badge';
    }
  };

  if (!open || !submission) return null;

  const labelStyle: React.CSSProperties = { fontWeight: 700 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content kyc-details-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        {/* Header */}
        <div className="modal-header">
          <div className="kyc-header-content">
            <div className="user-info">
              <div className="user-avatar">
                <span className="user-icon">👤</span>
              </div>
              <div className="user-details">
                <h2>{submission.userName}</h2>
                <p className="user-email">{submission.email}</p>
                <p className="user-type">Individual Account</p>
                <span className={getStatusBadgeClass(submission.status)}>
                  {submission.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs as cards */}
        <div className="tab-card-grid">
          <button className={tabValue === 0 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(0)}>
            <span className="tab-emoji">👤</span>
            <div>
              <div className="tab-title">Personal Information</div>
              <div className="tab-sub">Profile, ID, contact</div>
            </div>
          </button>
          <button className={tabValue === 1 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(1)}>
            <span className="tab-emoji">📎</span>
            <div>
              <div className="tab-title">Documents</div>
              <div className="tab-sub">Passport, utility, ID</div>
            </div>
          </button>
          <button className={tabValue === 2 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(2)}>
            <span className="tab-emoji">💼</span>
            <div>
              <div className="tab-title">Employment & Financial</div>
              <div className="tab-sub">Income, funds, wealth</div>
            </div>
          </button>
          <button className={tabValue === 3 ? 'tab-card active' : 'tab-card'} onClick={() => handleTabChange(3)}>
            <span className="tab-emoji">📝</span>
            <div>
              <div className="tab-title">Review</div>
              <div className="tab-sub">Status & metadata</div>
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <TabPanel value={tabValue} index={0}>
            <div className="kyc-info-section">
              <h3 style={{ fontWeight: 800 }}>Personal Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={labelStyle}>User ID</label>
                  <span>{submission.userId || submission.id}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Full Name</label>
                  <span>{submission.firstName && submission.lastName ? `${submission.firstName} ${submission.lastName}` : submission.userName}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Email</label>
                  <span>{submission.email}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Phone Number</label>
                  <span>{submission.phoneNumber || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Date of Birth</label>
                  <span>{submission.dateOfBirth ? formatDate(submission.dateOfBirth) : 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Gender</label>
                  <span>{submission.gender || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Nationality</label>
                  <span>{submission.nationality || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>ID Number</label>
                  <span>{submission.idNumber || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>ID Country of Issue</label>
                  <span>{submission.idCountryOfIssue || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>ID Date of Expiry</label>
                  <span>{submission.idDateOfExpiry ? formatDate(submission.idDateOfExpiry) : 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Social Insurance Number</label>
                  <span>{submission.socialInsuranceNumber || 'Not provided'}</span>
                </div>
                {submission.address && (
                  <div className="info-item full-width">
                    <label style={labelStyle}>Address</label>
                    <span>
                      {submission.address.street}, {submission.address.city}, {submission.address.state} {submission.address.postalCode}, {submission.address.country}
                    </span>
                  </div>
                )}
                <div className="info-item">
                  <label style={labelStyle}>Submission ID</label>
                  <span>{submission.id}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Submitted At</label>
                  <span>{formatDate(submission.submittedAt)}</span>
                </div>
                {/* <div className="info-item">
                  <label>Status</label>
                  <span className={getStatusBadgeClass(submission.status)}>
                    {submission.status}
                  </span>
                </div> */}
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <div className="kyc-info-section">
              <h3 style={{ fontWeight: 800 }}>Submitted Documents</h3>
              <div className="documents-grid">
                <div className="document-item">
                  <div className="document-icon">📄</div>
                  <div className="document-info">
                    <h4>Utility Bill</h4>
                    <span className={`document-status ${submission.utilityBillPath ? 'submitted' : 'missing'}`}>
                      {submission.utilityBillPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.utilityBillPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.utilityBillPath}`,
                          fileName: submission.utilityBillPath.split('/').pop() || 'Utility Bill',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">🛂</div>
                  <div className="document-info">
                    <h4>Passport</h4>
                    <span className={`document-status ${submission.passportPath ? 'submitted' : 'missing'}`}>
                      {submission.passportPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.passportPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.passportPath}`,
                          fileName: submission.passportPath.split('/').pop() || 'Passport',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">🆔</div>
                  <div className="document-info">
                    <h4>ID Front</h4>
                    <span className={`document-status ${submission.idFrontPath ? 'submitted' : 'missing'}`}>
                      {submission.idFrontPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.idFrontPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.idFrontPath}`,
                          fileName: submission.idFrontPath.split('/').pop() || 'ID Front',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="document-item">
                  <div className="document-icon">🆔</div>
                  <div className="document-info">
                    <h4>ID Back</h4>
                    <span className={`document-status ${submission.idBackPath ? 'submitted' : 'missing'}`}>
                      {submission.idBackPath ? '✅ Submitted' : '❌ Missing'}
                    </span>
                    {submission.idBackPath && (
                      <button
                        onClick={() => setPreviewFile({
                          isOpen: true,
                          url: `${API_BASE}/admin/files/${submission.idBackPath}`,
                          fileName: submission.idBackPath.split('/').pop() || 'ID Back',
                          contentType: 'application/pdf'
                        })}
                        className="document-link"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', font: 'inherit' }}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
                
                {/* ID Card Back has been merged into ID Back */}
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <div className="kyc-info-section">
              <h3 style={{ fontWeight: 800 }}>Employment & Financial Information</h3>
              <div className="info-grid">
                {submission.employment && (
                  <>
                    <div className="info-item">
                      <label style={labelStyle}>Employer Name</label>
                      <span>{submission.employment.employerName || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label style={labelStyle}>Job Title</label>
                      <span>{submission.employment.jobTitle || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label style={labelStyle}>Monthly Income</label>
                      <span>{submission.employment.monthlyIncome || 'Not provided'}</span>
                    </div>
                    <div className="info-item">
                      <label style={labelStyle}>Employment Duration</label>
                      <span>{submission.employment.duration || 'Not provided'}</span>
                    </div>
                  </>
                )}
                <div className="info-item">
                  <label style={labelStyle}>Source of Funds</label>
                  <span>{submission.sourceOfFunds || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Purpose of Account</label>
                  <span>{submission.purposeOfAccount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Annual Income Range</label>
                  <span>{submission.annualIncomeRange || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Earnings Amount</label>
                  <span>{submission.earningsAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Investment Income Amount</label>
                  <span>{submission.investmentIncomeAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Real Estate Income Amount</label>
                  <span>{submission.realEstateIncomeAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Allowances Amount</label>
                  <span>{submission.allowancesAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Other Income Amount</label>
                  <span>{submission.otherIncomeAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Other Income Specify</label>
                  <span>{submission.otherIncomeSpecify || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Country of Origin Income</label>
                  <span>{submission.countryOfOriginIncome || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Wealth Size Range</label>
                  <span>{submission.wealthSizeRange || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Employment Wealth Amount</label>
                  <span>{submission.employmentWealthAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Other Investments Amount</label>
                  <span>{submission.otherInvestmentsAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Business Interests Amount</label>
                  <span>{submission.businessInterestsAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Real Estate Wealth Amount</label>
                  <span>{submission.realEstateWealthAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Pension Funds Amount</label>
                  <span>{submission.pensionFundsAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Inheritance Gifts Amount</label>
                  <span>{submission.inheritanceGiftsAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Other Wealth Amount</label>
                  <span>{submission.otherWealthAmount || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Other Wealth Specify</label>
                  <span>{submission.otherWealthSpecify || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Country of Origin Wealth</label>
                  <span>{submission.countryOfOriginWealth || 'Not provided'}</span>
                </div>
                {submission.sourceOfFundsExplanation && (
                  <div className="info-item full-width">
                    <label style={labelStyle}>Source of Funds Explanation</label>
                    <span>{submission.sourceOfFundsExplanation}</span>
                  </div>
                )}
                {submission.sourceOfWealthExplanation && (
                  <div className="info-item full-width">
                    <label style={labelStyle}>Source of Wealth Explanation</label>
                    <span>{submission.sourceOfWealthExplanation}</span>
                  </div>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <div className="kyc-info-section">
              <h3 style={{ fontWeight: 800 }}>Review Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label style={labelStyle}>Status</label>
                  <span className={getStatusBadgeClass(submission.status)}>
                    {submission.status}
                  </span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Submitted At</label>
                  <span>
                    {submission.submittedAt ? formatDate(submission.submittedAt) : 'Not available'}
                  </span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>Submission ID</label>
                  <span>{submission.id}</span>
                </div>
                <div className="info-item">
                  <label style={labelStyle}>User ID</label>
                  <span>{submission.userId}</span>
                </div>
              </div>
            </div>
          </TabPanel>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {/* <button className="action-btn secondary" onClick={onClose}>
            Close
          </button> */}
        </div>

        {/* File Preview Modal */}
        <FilePreviewModal
          isOpen={previewFile.isOpen}
          onClose={() => setPreviewFile({ isOpen: false, url: '', fileName: '', contentType: undefined })}
          fileUrl={previewFile.url}
          fileName={previewFile.fileName}
          contentType={previewFile.contentType}
        />
      </div>
    </div>
  );
};

export default KycDetailsModal;
